import { useMemo } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, Info, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ExpandableRow } from "@/components/ui/responsive-table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAdminContext } from "@/lib/admin-context";

import { PlatformBadge } from "./ads-badges";
import { formatCount, formatIsoDay, formatMinor, rangeForDays } from "./format";
import { ADS_QUERY_PREFIX } from "./use-ad-connections";
import {
  AD_PLATFORMS,
  PLATFORM_LABEL,
  normalizeOverview,
  type AdPlatform,
  type AdsOverview,
  type AdsPlatformSummary,
} from "./types";

/**
 * Admin → Marketing → Ads → Overview.
 *
 *   GET /api/admin/ads/overview?from=YYYY-MM-DD&to=YYYY-MM-DD
 *
 * Per-platform spend next to what that spend bought in OUR database — claimed
 * profiles and first paid subscriptions — so "cost per claimed profile" is a
 * number the admin can act on, not the platform's own conversion count. Both
 * are shown because they measure different things: the platform counts its
 * pixel events, we count B2B outcomes.
 *
 * No chart library: the daily table is the honest view at this data volume,
 * and apps/admin does not ship one.
 */

export const RANGE_PRESETS = [7, 30, 90] as const;
export type RangePreset = (typeof RANGE_PRESETS)[number];

interface DailyTable {
  /** Platforms that have at least one daily row, in display order. */
  present: AdPlatform[];
  currencyOf: (platform: AdPlatform) => string | null;
  /** A cross-platform total is only meaningful when every column is one currency. */
  canTotal: boolean;
  totalCurrency: string | null;
  dates: Array<{ date: string; spend: Partial<Record<AdPlatform, number>> }>;
}

/** Long rows (`{ date, platform, spendMinor }`) → one row per day, newest first. */
function buildDailyTable(overview: AdsOverview | undefined): DailyTable {
  const rows = overview?.daily ?? [];
  const summaries = overview?.platforms ?? [];
  const present = AD_PLATFORMS.filter((p) => rows.some((r) => r.platform === p));
  const currencyOf = (p: AdPlatform): string | null =>
    summaries.find((s) => s.platform === p)?.currency ??
    rows.find((r) => r.platform === p && r.currency)?.currency ??
    null;
  const currencies = new Set(present.map(currencyOf));
  const byDate = new Map<string, Partial<Record<AdPlatform, number>>>();
  for (const r of rows) {
    if (!r.platform) continue;
    const bucket = byDate.get(r.date) ?? {};
    bucket[r.platform] = (bucket[r.platform] ?? 0) + r.spendMinor;
    byDate.set(r.date, bucket);
  }
  // A null currency means the API saw mixed currencies inside one platform —
  // never add those up, not even across a single column.
  const single = currencies.size === 1 ? ([...currencies][0] ?? null) : null;
  return {
    present,
    currencyOf,
    canTotal: present.length > 0 && single !== null,
    totalCurrency: single,
    dates: [...byDate.keys()]
      .sort((a, b) => b.localeCompare(a))
      .map((date) => ({ date, spend: byDate.get(date) ?? {} })),
  };
}

interface AdsOverviewTabProps {
  range: RangePreset;
  onRangeChange: (range: RangePreset) => void;
  onGoToAccounts: () => void;
}

export function AdsOverviewTab({ range, onRangeChange, onGoToAccounts }: AdsOverviewTabProps) {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const { dbTarget } = useAdminContext();
  const { from, to } = useMemo(() => rangeForDays(range), [range]);

  const overviewQuery = useQuery({
    queryKey: [...ADS_QUERY_PREFIX, "overview", from, to, dbTarget],
    queryFn: async () => {
      const params = new URLSearchParams({ from, to });
      return normalizeOverview(await apiFetch.get<unknown>(`/api/admin/ads/overview?${params}`));
    },
    staleTime: 60_000,
  });

  const overview = overviewQuery.data;
  const platforms = overview?.platforms ?? [];
  const table = useMemo(() => buildDailyTable(overview), [overview]);

  const rangeLabel = (preset: RangePreset): string => {
    switch (preset) {
      case 7:
        return t`7 days`;
      case 30:
        return t`30 days`;
      case 90:
        return t`90 days`;
    }
  };

  const fromLabel = formatIsoDay(from, locale);
  const toLabel = formatIsoDay(to, locale);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div
          role="group"
          aria-label={t`Date range`}
          className="border-border bg-muted/40 flex flex-wrap rounded-lg border p-1"
        >
          {RANGE_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              aria-pressed={range === preset}
              onClick={() => onRangeChange(preset)}
              className={`focus-visible:ring-ring/50 min-h-[44px] rounded-md px-3 text-sm font-medium transition-colors focus-visible:ring-3 focus-visible:outline-none ${
                range === preset
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {rangeLabel(preset)}
            </button>
          ))}
        </div>
        <p className="text-muted-foreground text-xs">
          <Trans>
            {fromLabel} – {toLabel}
          </Trans>
        </p>
      </div>

      {overviewQuery.isError ? (
        <Card>
          <CardContent className="space-y-3 p-6 text-center text-sm">
            <p className="text-muted-foreground">
              {overviewQuery.error instanceof ApiError ? (
                <Trans>Could not load the ads overview ({overviewQuery.error.status}).</Trans>
              ) : (
                <Trans>Could not load the ads overview.</Trans>
              )}
            </p>
            <Button
              variant="outline"
              className="min-h-[44px] px-4"
              onClick={() => void overviewQuery.refetch()}
            >
              <Trans>Try again</Trans>
            </Button>
          </CardContent>
        </Card>
      ) : overviewQuery.isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Skeleton className="h-56" />
          <Skeleton className="h-56" />
          <Skeleton className="h-64 md:col-span-2" />
        </div>
      ) : platforms.length === 0 ? (
        <Card>
          <CardContent className="space-y-3 p-8 text-center">
            <BarChart3 className="text-muted-foreground mx-auto size-8" />
            <p className="text-muted-foreground text-sm">
              <Trans>
                No ad spend in this range. Connect an ad account and run a sync to see spend next to
                claimed profiles and paid subscriptions.
              </Trans>
            </p>
            <Button variant="outline" className="min-h-[44px] px-4" onClick={onGoToAccounts}>
              <Plus className="size-4" />
              <Trans>Go to accounts</Trans>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {platforms.map((summary) => (
              <PlatformCard key={summary.platform} summary={summary} locale={locale} />
            ))}
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                <Trans>Daily spend</Trans>
              </CardTitle>
              {!table.canTotal && table.present.length > 0 && (
                <p className="text-muted-foreground text-xs">
                  <Trans>
                    The accounts bill in more than one currency, so no total is shown — compare each
                    platform on its own.
                  </Trans>
                </p>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {table.dates.length === 0 ? (
                <p className="text-muted-foreground px-4 pb-4 text-sm">
                  <Trans>No daily rows in this range.</Trans>
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          <Trans>Date</Trans>
                        </TableHead>
                        {table.present.map((p) => (
                          <TableHead key={p} className="hidden text-right md:table-cell">
                            {PLATFORM_LABEL[p]}
                          </TableHead>
                        ))}
                        <TableHead className="text-right">
                          <Trans>Total</Trans>
                        </TableHead>
                        <TableHead className="w-8 md:hidden" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {table.dates.map(({ date, spend }) => {
                        const total = table.present.reduce((sum, p) => sum + (spend[p] ?? 0), 0);
                        return (
                          <ExpandableRow
                            key={date}
                            mobileDetails={table.present.map((p) => ({
                              label: PLATFORM_LABEL[p],
                              value: formatMinor(spend[p] ?? 0, table.currencyOf(p), locale),
                            }))}
                          >
                            <TableCell className="text-sm whitespace-nowrap">
                              {formatIsoDay(date, locale)}
                            </TableCell>
                            {table.present.map((p) => (
                              <TableCell
                                key={p}
                                className="hidden text-right text-sm tabular-nums md:table-cell"
                              >
                                {formatMinor(spend[p] ?? 0, table.currencyOf(p), locale)}
                              </TableCell>
                            ))}
                            <TableCell className="text-right text-sm font-medium tabular-nums">
                              {table.canTotal
                                ? formatMinor(total, table.totalCurrency, locale)
                                : "—"}
                            </TableCell>
                          </ExpandableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {(overview?.notes.length ?? 0) > 0 && (
        <section
          aria-label={t`Notes`}
          className="border-info-border bg-info-soft space-y-2 rounded-lg border p-3"
        >
          <p className="text-info-strong flex items-center gap-2 text-sm font-semibold">
            <Info className="size-4 shrink-0" />
            <Trans>Notes</Trans>
          </p>
          <ul className="text-info-strong list-disc space-y-1 pl-6 text-xs">
            {overview?.notes.map((note, i) => (
              <li key={`${i}-${note.slice(0, 24)}`}>{note}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function PlatformCard({ summary, locale }: { summary: AdsPlatformSummary; locale: string }) {
  const { t } = useLingui();
  const money = (minor: number | null) => formatMinor(minor, summary.currency, locale);
  const count = (value: number | null) => formatCount(value, locale);

  const metrics: Array<{ key: string; label: string; value: string; emphasis?: boolean }> = [
    { key: "spend", label: t`Spend`, value: money(summary.spendMinor), emphasis: true },
    { key: "clicks", label: t`Clicks`, value: count(summary.clicks) },
    {
      key: "platformConversions",
      label: t`Platform conversions`,
      value: count(summary.platformConversions),
    },
    { key: "claimed", label: t`Claimed profiles`, value: count(summary.claimed) },
    { key: "paid", label: t`Paid subscriptions`, value: count(summary.paid) },
    {
      key: "costPerClaimed",
      label: t`Cost per claimed profile`,
      value: money(summary.costPerClaimedMinor),
      emphasis: true,
    },
    {
      key: "costPerPaid",
      label: t`Cost per paid subscription`,
      value: money(summary.costPerPaidMinor),
      emphasis: true,
    },
  ];

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center gap-2">
        <CardTitle className="text-base">{PLATFORM_LABEL[summary.platform]}</CardTitle>
        <PlatformBadge platform={summary.platform} />
        {summary.currency && (
          <span className="text-muted-foreground ml-auto text-xs">{summary.currency}</span>
        )}
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {metrics.map((m) => (
            <div key={m.key} className="border-border rounded-lg border p-2.5">
              <dt className="text-muted-foreground text-[0.6875rem] leading-tight">{m.label}</dt>
              <dd
                className={`mt-1 tabular-nums ${m.emphasis ? "text-foreground text-lg font-semibold" : "text-foreground text-base font-medium"}`}
              >
                {m.value}
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}
